import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { App, Form, Spin, Tabs } from "antd";
import { toJS } from "mobx";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createCopy } from "@devowl-wp/multilingual";
import { FormSettingsAffiliateTabContent, FormSettingsConsentTabContent, FormSettingsCountryBypassTabContent, FormSettingsFooter, FormSettingsGeneralTabContent, FormSettingsGoogleConsentModeTabContent, FormSettingsMultisiteTabContent, FormSettingsResetTabContent, FormSettingsTcfTabContent, useFormSettingsHandler, useFormSettingsProvider, useNavLinksPageTypes } from "@devowl-wp/react-cookie-banner-admin";
import { RouteHttpVerb } from "@devowl-wp/utils";
import { useLocationQuery } from "../../../hooks/useLocationQuery.js";
import { useRouteTcfVendorConfiguration } from "../../../hooks/useRouteTcfVendorConfiguration.js";
import { useStores } from "../../../store/stores.js";
import { __ } from "../../../utils/i18n.js";
import { request } from "../../../utils/request.js";
import { locationRestCreateCookiePolicyPost } from "../../../wp-api/createCookiePolicy.post.js";
import { locationRestForwardEndpointsGet } from "../../../wp-api/forwardEndpoints.get.js";
import { CSS_SPINNER_IN_CONTENT } from "../../configApp.js";
import { PostTypeSelect } from "../../postTypeSelect.js";
import { ConfigContent } from "../content.js";
import { ConfigHintModal } from "../hintModal.js";
const LAYOUT = {
    labelCol: {
        span: 6
    },
    wrapperCol: {
        span: 16
    }
};
/*const TAIL_LAYOUT: typeof LAYOUT = {
    wrapperCol: { offset: 8, span: 16 }
};*/ const SettingsForm = observer(()=>{
    const { message } = App.useApp();
    const tab = useParams().tab || "";
    const navigate = useNavigate();
    const { tcfIntegrationItem, navigateAfterTcfActivation } = useLocationQuery();
    const { link: tcfLink } = useRouteTcfVendorConfiguration();
    const { optionStore, checklistStore, tcfStore } = useStores();
    const availablePageTypesNonMultiple = useNavLinksPageTypes().map(({ multiple, name })=>!multiple && name).filter(Boolean);
    const firstTimeSave = useMemo(()=>checklistStore.items.filter(({ id })=>id === "save-settings")[0]?.checked === false, [
        checklistStore
    ]);
    const { fetchedBannerLinks, busySettings, areSettingsFetched, isBannerActive, isBlockerActive, hidePageIds, setCookiesViaManager, operatorCountry, operatorContactAddress, operatorContactEmail, operatorContactPhone, operatorContactFormId, cookiePolicyId, territorialLegalBasis, isAcceptAllForBots, isRespectDoNotTrack, isBannerLessConsent, bannerLessConsentShowOnPageIds, cookieDuration, failedConsentDocumentationHandling, isSaveIp, isDataProcessingInUnsafeCountries, isAgeNotice, ageNoticeAgeLimit, isListServicesNotice, isConsentForwarding, forwardTo, crossDomains, affiliateLink, affiliateLabelBehind, affiliateLabelDescription, isCountryBypass, countryBypassCountries, countryBypassType, isTcf, tcfAcceptedTime, tcfGvlDownloadTime, isGcm, isGcmShowRecommandationsWithoutConsent, isGcmCollectAdditionalDataViaUrlParameters, isGcmRedactAdsDataWithoutConsent, isGcmListPurposes, consentDuration, consentsDeletedAt, bannerlessConsentChecks, countryBypassDbDownloadTime, others: { customizeValuesBanner: { texts: { ageNoticeBanner } }, iso3166OneAlpha2, ageNoticeCountryAgeMap, predefinedCountryBypassLists, frontend: { predefinedDataProcessingInSafeCountriesLists }, customizeBannerUrl, adminUrl, resetUrl, resetTexts: { dry: resetTextsLanguages, url: resetTextsUrl } } } = optionStore;
    const { prompt, form, isBusy, defaultValues, onFinish, onFinishFailed, onValuesChange, contextValue } = useFormSettingsHandler({
        isCountryBypass,
        isTcf,
        isGcm,
        unloadPromptWhen: ({ nextLocation: { pathname } })=>!pathname.startsWith("/settings"),
        handleSave: async (values)=>{
            await optionStore.updateSettings({
                ...initialValues,
                ...values
            }, values.navLinks);
            // Automatically redirect to next tab after first change of settings
            if (firstTimeSave) {
                const tabs = [
                    "",
                    "consent",
                    "country-bypass"
                ];
                const currentIdx = tabs.indexOf(tab);
                if (currentIdx > -1 && tabs[currentIdx + 1]) {
                    navigate(`/settings/${tabs[currentIdx + 1]}`);
                }
            }
            // Allow to redirect via URL parameter when activating TCF successfully
            if (navigateAfterTcfActivation && optionStore.tcfGvlDownloadTime) {
                window.location.href = navigateAfterTcfActivation;
            }
        }
    });
    const handleValuesChange = useCallback((changedValues, values)=>{
        if (Object.hasOwn(changedValues, "operatorCountry") && values.operatorCountry === "CH") {
            form.setFieldValue("territorialLegalBasis", [
                "gdpr-eprivacy",
                "dsg-switzerland"
            ]);
        }
        onValuesChange(changedValues, values);
    }, [
        onValuesChange
    ]);
    useEffect(()=>{
        optionStore.fetchBannerLinks();
    }, []);
    const renderPageSelector = useCallback(({ selectedPageContent, selectedPageUrl, onlyPages, ...rest })=>/*#__PURE__*/ _jsx(PostTypeSelect, {
            ...rest,
            postType: onlyPages ? [
                "page"
            ] : undefined,
            forceDefaultLanguage: true,
            applyTitleRenderOnSelectOption: true,
            titleRender: (post, selected)=>{
                const { id, title: { rendered }, content: { rendered: contentRendered }, link } = post || {
                    id: 0,
                    title: {
                        rendered: undefined
                    },
                    content: {
                        rendered: ""
                    }
                };
                // Missuses `titleRender` to check if the current selected privacy policy has
                // a sentence about Real Cookie Banner.
                if (selectedPageContent) {
                    if (selected.indexOf(id) > -1) {
                        selectedPageContent(contentRendered);
                    } else if (selected.length === 0) {
                        selectedPageContent(undefined);
                    }
                }
                if (selectedPageUrl) {
                    selectedPageUrl(selected.indexOf(id) > -1 ? link : undefined);
                }
                return rendered;
            },
            perPage: 100
        }), []);
    const navLinksFromStoreToFormValue = useCallback((links)=>links.map(({ data: { id, title: { rendered: label }, meta: { pageType, isExternalUrl, pageId, externalUrl, hideCookieBanner, isTargetBlank }, multilingual } }, i)=>({
                id,
                // Are there duplicate page types which are not allowed to be available multiple times (e.g. privacy policy when deactivating WPML/PolyLang)
                pageType: availablePageTypesNonMultiple.indexOf(pageType) > -1 && links.some(({ data: { meta: { pageType: thisPageType } } }, thisI)=>thisI < i && pageType === thisPageType) ? "other" : pageType,
                isExternalUrl,
                pageId,
                externalUrl,
                hideCookieBanner,
                isTargetBlank,
                label,
                languages: toJS(multilingual),
                languageOnClick: async (recordId, { code, id })=>{
                    try {
                        if (id === false) {
                            await createCopy("rcb-banner-links", recordId, code);
                        }
                        // Open the newly created link
                        const url = new URL(window.location.href);
                        url.searchParams.set("lang", code);
                        window.location.href = url.toString();
                    } catch (e) {
                        if (e.responseJSON?.message) {
                            message.error(e.responseJSON.message);
                        } else {
                            throw e;
                        }
                    }
                }
            })), []);
    const [FormSettingsContextProvider, formSettingsContextValue] = useFormSettingsProvider({
        ...contextValue,
        onPageEditClick: (id)=>window.open(`${adminUrl}post.php?post=${id}&action=edit`, "_blank"),
        activeTab: tab,
        resetTextsLanguages,
        onResetTexts: (languages)=>{
            window.location.href = `${resetTextsUrl}&${languages.map((lang)=>`reset-lang[]=${lang}`).join("&")}`;
        },
        onResetAll: (resetConsents)=>{
            window.location.href = `${resetUrl}&${resetConsents ? "&reset-consents" : ""}`;
        },
        onStartTcfVendorConfiguration: ()=>{
            window.location.href = tcfLink;
        },
        renderPageSelector,
        onHasPrivacyPolicyRCBSentenceChanged: ()=>{
            checklistStore.probablyFetchByChangedItem("privacy-policy-mention-usage");
        },
        consentsDeletedAt,
        iso3166OneAlpha2,
        ageNotice: ageNoticeBanner,
        ageNoticeCountryAgeMap,
        predefinedCountryBypassLists,
        predefinedDataProcessingInSafeCountriesLists,
        onCustomizeClick: (id)=>window.open(`${customizeBannerUrl.replace(/autofocus\[panel]=[\w-]+/, `autofocus[control]=${id}`)}&return=${encodeURIComponent(window.location.href)}`, "_blank"),
        countryBypassDbDownloadTime,
        tcfAcceptedTime,
        tcfGvlDownloadTime,
        multisiteEndpoints: {
            current: {},
            forwardTo: {}
        },
        bannerlessConsentChecks
    }, {
        onCountryBypassUpdateDatabase: ()=>optionStore.updateCountryBypassDatabase(),
        onTcfUpdateDatabase: ()=>tcfStore.updateGvl(),
        fetchMultisiteEndpoints: async ({ set })=>{
            const [current, forwardTo] = await Promise.all([
                request({
                    location: locationRestForwardEndpointsGet,
                    params: {
                        filter: "onlyCurrent"
                    }
                }),
                request({
                    location: locationRestForwardEndpointsGet,
                    params: {
                        filter: "notCurrent"
                    }
                })
            ]);
            set({
                multisiteEndpoints: {
                    current,
                    forwardTo
                }
            });
        },
        createCookiePolicy: async ()=>{
            const { id } = await request({
                location: locationRestCreateCookiePolicyPost
            });
            return {
                pageId: id,
                onNewNavLinks: (navLinks)=>optionStore.updateSettings({}, navLinks)
            };
        },
        deactivateCookiePolicy: async (_, id)=>{
            await request({
                location: {
                    path: `/pages/${id}`,
                    method: RouteHttpVerb.DELETE,
                    namespace: "wp/v2"
                },
                request: {
                    force: true
                }
            });
            return {
                onNewNavLinks: (navLinks)=>optionStore.updateSettings({}, navLinks)
            };
        }
    }, {
        inherit: [
            "isCountryBypass",
            "isGcm",
            "isTcf",
            "tcfGvlDownloadTime",
            "countryBypassDbDownloadTime",
            "activeTab"
        ]
    });
    if (!fetchedBannerLinks || !areSettingsFetched) {
        return /*#__PURE__*/ _jsx(Spin, {
            spinning: true,
            style: CSS_SPINNER_IN_CONTENT
        });
    }
    const initialValues = {
        ...defaultValues,
        isBannerActive,
        isBlockerActive,
        hidePageIds: [
            ...hidePageIds
        ],
        setCookiesViaManager,
        operatorCountry,
        operatorContactAddress,
        operatorContactEmail,
        operatorContactPhone,
        operatorContactFormId,
        cookiePolicyId,
        territorialLegalBasis,
        isAcceptAllForBots,
        isRespectDoNotTrack,
        isBannerLessConsent,
        bannerLessConsentShowOnPageIds: [
            ...bannerLessConsentShowOnPageIds
        ],
        cookieDuration,
        failedConsentDocumentationHandling,
        isSaveIp,
        isDataProcessingInUnsafeCountries,
        isAgeNotice,
        ageNoticeAgeLimit,
        isListServicesNotice,
        isGcmShowRecommandationsWithoutConsent,
        isGcmCollectAdditionalDataViaUrlParameters,
        isGcmRedactAdsDataWithoutConsent,
        isGcmListPurposes,
        isConsentForwarding,
        forwardTo: [
            ...forwardTo
        ],
        crossDomains,
        affiliateLink,
        affiliateLabelBehind,
        affiliateLabelDescription,
        countryBypassCountries: [
            ...countryBypassCountries
        ],
        countryBypassType,
        consentDuration,
        navLinks: navLinksFromStoreToFormValue(optionStore.bannerLinks.sortedBannerLinks)
    };
    return /*#__PURE__*/ _jsx(FormSettingsContextProvider, {
        value: formSettingsContextValue,
        children: /*#__PURE__*/ _jsxs(Spin, {
            spinning: busySettings || isBusy,
            children: [
                prompt,
                /*#__PURE__*/ _jsx(Form, {
                    name: "settings",
                    initialValues: initialValues,
                    form: form,
                    ...LAYOUT,
                    onFinish: onFinish,
                    onValuesChange: handleValuesChange,
                    onFinishFailed: onFinishFailed,
                    scrollToFirstError: {
                        behavior: "smooth",
                        block: "center"
                    },
                    labelWrap: true,
                    children: /*#__PURE__*/ _jsx(Form.Item, {
                        noStyle: true,
                        labelCol: {
                            span: 0
                        },
                        wrapperCol: {
                            span: 24
                        },
                        children: /*#__PURE__*/ _jsx(Tabs, {
                            activeKey: tab,
                            onTabClick: (key)=>{
                                navigate(`/settings/${key}`);
                            },
                            items: [
                                {
                                    key: "",
                                    label: __("General"),
                                    children: /*#__PURE__*/ _jsxs(ConfigContent, {
                                        maxWidth: "fixed",
                                        children: [
                                            /*#__PURE__*/ _jsx(FormSettingsGeneralTabContent, {}),
                                            /*#__PURE__*/ _jsx(FormSettingsFooter, {})
                                        ]
                                    })
                                },
                                {
                                    key: "consent",
                                    label: __("Consent"),
                                    children: /*#__PURE__*/ _jsxs(ConfigContent, {
                                        maxWidth: "fixed",
                                        children: [
                                            /*#__PURE__*/ _jsx(FormSettingsConsentTabContent, {}),
                                            /*#__PURE__*/ _jsx(FormSettingsFooter, {})
                                        ]
                                    })
                                },
                                {
                                    key: "country-bypass",
                                    label: __("Geo-restriction"),
                                    children: /*#__PURE__*/ _jsxs(ConfigContent, {
                                        maxWidth: "fixed",
                                        children: [
                                            /*#__PURE__*/ _jsx(FormSettingsCountryBypassTabContent, {}),
                                            /*#__PURE__*/ _jsx(FormSettingsFooter, {})
                                        ]
                                    })
                                },
                                {
                                    key: "tcf",
                                    label: __("Transparency & Consent Framework (TCF)"),
                                    children: /*#__PURE__*/ _jsxs(ConfigContent, {
                                        maxWidth: "fixed",
                                        children: [
                                            /*#__PURE__*/ _jsx(FormSettingsTcfTabContent, {}),
                                            /*#__PURE__*/ _jsx(FormSettingsFooter, {})
                                        ]
                                    })
                                },
                                {
                                    key: "gcm",
                                    label: __("Google Consent Mode"),
                                    children: /*#__PURE__*/ _jsxs(ConfigContent, {
                                        maxWidth: "fixed",
                                        children: [
                                            /*#__PURE__*/ _jsx(FormSettingsGoogleConsentModeTabContent, {}),
                                            /*#__PURE__*/ _jsx(FormSettingsFooter, {})
                                        ]
                                    })
                                },
                                {
                                    key: "multisite",
                                    label: __("Consent Forwarding"),
                                    children: /*#__PURE__*/ _jsxs(ConfigContent, {
                                        maxWidth: "fixed",
                                        children: [
                                            /*#__PURE__*/ _jsx(FormSettingsMultisiteTabContent, {}),
                                            /*#__PURE__*/ _jsx(FormSettingsFooter, {})
                                        ]
                                    })
                                },
                                process.env.PLUGIN_CTX === "pro" && tab === "affiliate" && {
                                    key: "affiliate",
                                    label: __("Affiliate"),
                                    children: /*#__PURE__*/ _jsxs(ConfigContent, {
                                        maxWidth: "fixed",
                                        children: [
                                            /*#__PURE__*/ _jsx(FormSettingsAffiliateTabContent, {}),
                                            /*#__PURE__*/ _jsx(FormSettingsFooter, {})
                                        ]
                                    })
                                },
                                tab === "reset" && {
                                    key: "reset",
                                    label: __("Reset"),
                                    children: /*#__PURE__*/ _jsx(FormSettingsResetTabContent, {})
                                }
                            ].filter(Boolean)
                        })
                    })
                }),
                tcfIntegrationItem && /*#__PURE__*/ _jsx(ConfigHintModal, {
                    always: true,
                    identifier: "tcf-integration",
                    title: __("Consent required according to TCF"),
                    sprintfArgs: [
                        tcfIntegrationItem
                    ]
                })
            ]
        })
    });
});
export { SettingsForm };
